import { fastExtractionPassages } from "@/features/data-curation/api/curation";
import type {
  CurationColumn,
  ExtractionCell,
  ExtractionRow,
  ExtractionSourceSpan,
} from "@/features/data-curation/types";
import { SpanPassage, type Span } from "@/features/fast-dataset/components/EvidencePanel";
import { useQueries } from "@tanstack/react-query";
import { OriginalSourceLink } from "@/features/data-curation/components/OriginalSourceLink";
import { X } from "lucide-react";
import { useMemo } from "react";

type CellSpan = ExtractionSourceSpan & { doc: string; columnId: string };

const docOf = (path: string) => (path.startsWith("/papers/") ? path.slice("/papers/".length) : null);

const cellValue = (cell: ExtractionCell) => {
  const text = cell.as_reported || (cell.value == null ? "" : String(cell.value));
  return text || (cell.segments.length ? cell.segments.map((s) => s.as_reported || String(s.value ?? "")).join(" / ") : "");
};

const spansOf = (cell: ExtractionCell): CellSpan[] =>
  [cell, ...cell.segments].flatMap((part) =>
    part.source_locations.flatMap((location) => {
      const doc = docOf(location.path);
      return location.span && doc ? [{ ...location.span, doc, columnId: cell.column_id }] : [];
    }),
  );

type Props = {
  datasetId: string;
  row: ExtractionRow;
  columns: CurationColumn[];
  focusColumnId: string | null;
  onFocusColumn: (columnId: string | null) => void;
  onClose: () => void;
};

/**
 * The details of a row of a Fast dataset: the passages its cells came from, with each cell's
 * span marked; the focused cell's span stands out. Without the passages, the cited excerpts.
 */
export const FastDetailsPanel = ({ datasetId, row, columns, focusColumnId, onFocusColumn, onClose }: Props) => {
  const label = (columnId: string) => columns.find((c) => c.column_id === columnId)?.label ?? columnId;
  const cells = useMemo(
    () => new Map(row.cells.filter((cell) => cellValue(cell)).map((cell) => [cell.column_id, cell])),
    [row],
  );
  const spans = useMemo(() => [...cells.values()].flatMap(spansOf), [cells]);
  const docs = useMemo(() => {
    const primary = row.sources.find((s) => s.is_primary)?.source_id;
    return [...new Set([...(primary ? [primary] : []), ...spans.map((s) => s.doc)])];
  }, [row, spans]);
  const passages = useQueries({
    queries: docs.map((doc) => ({
      queryKey: ["fast-passages", datasetId, doc],
      queryFn: () => fastExtractionPassages(datasetId, doc),
      staleTime: Infinity,
      retry: false,
    })),
  });
  const toSpan = (s: CellSpan, offset = 0): Span => ({
    start: s.start - offset,
    end: s.end - offset,
    key: s.columnId,
    label: label(s.columnId),
    confidence: 1,
  });
  const source = row.sources.find((s) => s.is_primary) ?? row.sources[0];
  const focused = focusColumnId ? cells.get(focusColumnId) : undefined;

  return (
    <aside className="flex h-full flex-col bg-white" aria-label="Row details">
      <div className="flex items-start justify-between gap-3 border-b border-zinc-200 px-5 py-4">
        <div className="min-w-0">
          <p className="font-ui text-2xs uppercase tracking-wider text-zinc-400">{row.row_ref}</p>
          <h2 className="mt-1 text-[15px] font-semibold leading-snug text-zinc-900">
            {passages[0]?.data?.title || source?.label || row.row_description}
          </h2>
          {source?.source_id && (
            <p className="font-ui mt-1 text-xs">
              <OriginalSourceLink
                docId={source.source_id}
                url={source.source_url?.startsWith("http") ? source.source_url : null}
                isPaper={source.source_type === "paper"}
              />
            </p>
          )}
        </div>
        <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-800" aria-label="Close details">
          <X className="h-4 w-4" />
        </button>
      </div>

      {focusColumnId && (
        <div className="border-b border-zinc-200 bg-zinc-50/60 px-5 py-2.5 text-xs text-zinc-600" role="status">
          <span className="font-semibold text-zinc-900">
            {label(focusColumnId)}
          </span>{" "}
          {focused
            ? spans.some((s) => s.columnId === focusColumnId)
              ? `“${cellValue(focused)}” · the exact text of the ${spans.find((s) => s.columnId === focusColumnId)?.passage || "passage"}`
              : `“${cellValue(focused)}” · no span was stored for this cell`
            : "· not found in the passages read"}
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
        {docs.map((doc, d) => {
          const query = passages[d];
          const docSpans = spans.filter((s) => s.doc === doc);
          if (query?.isPending) {
            return (
              <p key={doc} className="text-xs text-zinc-400" role="status">
                Loading the passages…
              </p>
            );
          }
          const list = query?.data?.passages ?? [];
          const fits = (s: CellSpan) => {
            const text = list[s.passage_index]?.text;
            return text != null && s.end <= text.length && text.slice(s.start, s.end) === s.excerpt.slice(s.start - s.excerpt_start, s.end - s.excerpt_start);
          };
          if (list.length) {
            const by = list.map((_, i) => docSpans.filter((s) => s.passage_index === i && fits(s)).map((s) => toSpan(s)));
            const missed = docSpans.filter((s) => !fits(s));
            return (
              <div key={doc} className="space-y-5">
                {list.map((passage, i) => (
                  <section key={i} aria-label={passage.label || `Passage ${i + 1}`}>
                    <h3 className="font-ui mb-1 text-2xs uppercase tracking-wider text-zinc-400">
                      {passage.label || `Passage ${i + 1}`}
                      {by[i]?.length ? "" : " · no cells"}
                    </h3>
                    <SpanPassage
                      text={passage.text}
                      spans={by[i] ?? []}
                      focusKey={focusColumnId}
                      onSelect={(key) => onFocusColumn(focusColumnId === key ? null : key)}
                    />
                  </section>
                ))}
                {missed.map((s, i) => (
                  <Excerpt key={`missed-${i}`} span={s} toSpan={toSpan} focusKey={focusColumnId} />
                ))}
              </div>
            );
          }
          return (
            <div key={doc} className="space-y-5">
              {docSpans.map((s, i) => (
                <Excerpt key={i} span={s} toSpan={toSpan} focusKey={focusColumnId} />
              ))}
            </div>
          );
        })}
        {!spans.length && (
          <p className="text-xs text-zinc-500">No cell of this row cites a span of a passage.</p>
        )}
      </div>
    </aside>
  );
};

/** The cited text around one cell's span, from a build whose passages are gone. */
const Excerpt = ({
  span,
  toSpan,
  focusKey,
}: {
  span: CellSpan;
  toSpan: (s: CellSpan, offset?: number) => Span;
  focusKey: string | null;
}) => (
  <section aria-label={span.passage || "Cited text"}>
    <h3 className="font-ui mb-1 text-2xs uppercase tracking-wider text-zinc-400">
      {span.passage || "Cited text"} · excerpt
    </h3>
    <SpanPassage text={span.excerpt} spans={[toSpan(span, span.excerpt_start)]} focusKey={focusKey} />
  </section>
);
